import { get, post } from '@/lib/api';

/* ================================================================== */
/*  Types                                                             */
/* ================================================================== */

export interface GitStatus {
  branch: string;
  ahead: number;
  behind: number;
  staged: GitFileChange[];
  unstaged: GitFileChange[];
  untracked: string[];
}

export interface GitFileChange {
  path: string;
  status: 'added' | 'modified' | 'deleted' | 'renamed';
  oldPath?: string;
}

export interface GitLogEntry {
  hash: string;
  shortHash: string;
  message: string;
  author: string;
  date: string;
}

export interface GitDiff {
  path: string;
  hunks: string;
}

/* ================================================================== */
/*  API calls                                                         */
/* ================================================================== */

const base = (wId: string) => `/ide/workspaces/${wId}/git`;

/** Get current branch, changed files, tracking info */
export async function getStatus(workspaceId: string): Promise<GitStatus> {
  return get<GitStatus>(`${base(workspaceId)}/status`);
}

/** Get recent commit history */
export async function getLog(
  workspaceId: string,
  limit = 20,
): Promise<GitLogEntry[]> {
  return get<GitLogEntry[]>(`${base(workspaceId)}/log?limit=${limit}`);
}

/** Stage files */
export async function stageFiles(
  workspaceId: string,
  paths: string[],
): Promise<void> {
  await post(`${base(workspaceId)}/stage`, { paths });
}

/** Unstage files */
export async function unstageFiles(
  workspaceId: string,
  paths: string[],
): Promise<void> {
  await post(`${base(workspaceId)}/unstage`, { paths });
}

/** Commit staged changes */
export async function commit(
  workspaceId: string,
  message: string,
): Promise<{ hash: string }> {
  return post<{ hash: string }>(`${base(workspaceId)}/commit`, { message });
}

/** Get diff for a file */
export async function getDiff(
  workspaceId: string,
  path: string,
): Promise<GitDiff> {
  const encoded = encodeURIComponent(path);
  return get<GitDiff>(`${base(workspaceId)}/diff/${encoded}`);
}

/** List branches */
export async function getBranches(
  workspaceId: string,
): Promise<{ branches: string[]; current: string }> {
  return get<{ branches: string[]; current: string }>(
    `${base(workspaceId)}/branches`,
  );
}

/** Switch branch */
export async function checkout(
  workspaceId: string,
  branch: string,
): Promise<void> {
  await post(`${base(workspaceId)}/checkout`, { branch });
}

/** Create a new branch */
export async function createBranch(
  workspaceId: string,
  name: string,
): Promise<void> {
  await post(`${base(workspaceId)}/branch`, { name });
}
